/**
 * SASADM-0170 — per-tenant app build override client (saas-auth SASATH-0387 §3 / §3A).
 *
 * Calls on `/api/saas-admin/tenants/{tenantId}/app-overrides`: list the tenant's PUBLIC
 * apps with the folder each is served from, set a custom build folder, revert to the
 * default, and turn the public app off/on. Standard `ApiResponse<T>` envelope, so this
 * uses the strict `request<ApiResponse<T>>()` + `unwrap()` pattern.
 *
 * Every write deploys routing; its outcome rides inside `data` as `routingDeploy`
 * (§3.1). The DELETE answers 200 `{ routingDeploy }`; a pre-0387 204/null body → null.
 *
 * SASADM-0171 (SASATH-0389 §3.2) — the offline page: `PUT/DELETE …/{appId}/offline-page`.
 */
import { request, unwrap } from '../client';
import type {
  ApiResponse,
  AppOverrideDto,
  RoutingDeployOutcome,
  WithRoutingDeploy,
} from '../types';

const base = (tenantId: number) => `/api/saas-admin/tenants/${tenantId}/app-overrides`;

export const appOverridesApi = {
  /** One row per PUBLIC (FRONTEND, TENANT-scope) app of the tenant's product, in app order. */
  async list(tenantId: number): Promise<AppOverrideDto[]> {
    const res = await request<ApiResponse<AppOverrideDto[]>>('GET', base(tenantId));
    return unwrap(res);
  },

  /** Serve this tenant the build in `docroot` for the app. */
  async set(tenantId: number, appId: number, docroot: string): Promise<AppOverrideDto> {
    const res = await request<ApiResponse<AppOverrideDto>>('PUT', `${base(tenantId)}/${appId}`, {
      json: { docroot },
    });
    return unwrap(res);
  },

  /** Revert to the default build (idempotent). Never re-enables a turned-off app. */
  async clear(tenantId: number, appId: number): Promise<RoutingDeployOutcome | null> {
    const res = await request<ApiResponse<WithRoutingDeploy | null> | undefined>(
      'DELETE',
      `${base(tenantId)}/${appId}`,
    );
    return res?.data?.routingDeploy ?? null;
  },

  /** §3A — turn this tenant's public app off or back on (idempotent). */
  async setEnabled(tenantId: number, appId: number, enabled: boolean): Promise<AppOverrideDto> {
    const res = await request<ApiResponse<AppOverrideDto>>(
      'PUT',
      `${base(tenantId)}/${appId}/enabled`,
      { json: { enabled } },
    );
    return unwrap(res);
  },

  /** SASATH-0389 §3.2 — the folder served while this tenant's public app is off. */
  async setOfflinePage(tenantId: number, appId: number, docroot: string): Promise<AppOverrideDto> {
    const res = await request<ApiResponse<AppOverrideDto>>(
      'PUT',
      `${base(tenantId)}/${appId}/offline-page`,
      { json: { docroot } },
    );
    return unwrap(res);
  },

  /** SASATH-0389 §3.2 — clear the offline page (200 with the row + `routingDeploy`). */
  async clearOfflinePage(tenantId: number, appId: number): Promise<RoutingDeployOutcome | null> {
    const res = await request<ApiResponse<WithRoutingDeploy | null> | undefined>(
      'DELETE',
      `${base(tenantId)}/${appId}/offline-page`,
    );
    return res?.data?.routingDeploy ?? null;
  },
};
